/** Sub-page 2: the relationships this node type takes part in, both directions. */

import { useState } from 'react'
import { EmptyState, Modal } from '@/shared/components'
import { useToast } from '@/shared/components/toast'
import { Field, SectionHeader } from '../../components'
import { useEdgeTypeMutations } from '../../hooks'
import {
  DATA_TYPES,
  type DataType,
  type EdgeTypeDef,
  type NodeTypeDef,
  type NodeTypeDetail,
} from '../../types/brain.types'

export function EdgesPanel({
  graphId,
  detail,
  nodeTypes,
}: {
  graphId: string
  nodeTypeId: string
  detail: NodeTypeDetail
  nodeTypes: NodeTypeDef[]
}) {
  const [editing, setEditing] = useState<EdgeTypeDef | 'new' | null>(null)
  const { remove } = useEdgeTypeMutations(graphId)
  const toast = useToast()

  const rows = [
    ...detail.edges_out.map((e) => ({ edge: e, direction: 'out' as const })),
    ...detail.edges_in.map((e) => ({ edge: e, direction: 'in' as const })),
  ]

  return (
    <div className="stack">
      <SectionHeader
        title="Relationships"
        hint="A relationship is a thing in its own right, with its own attributes — which is why the answer to 'who owns this account' can carry a role, a date and a confidence."
        action={
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setEditing('new')}
            disabled={nodeTypes.length < 1}
          >
            Add relationship
          </button>
        }
      />

      {rows.length === 0 ? (
        <EmptyState
          title="Nothing connects to this yet"
          action={
            <button className="btn btn-primary" onClick={() => setEditing('new')}>
              Add a relationship
            </button>
          }
        >
          Records of this type will still load — they just will not be reachable from anything
          else, so questions that span two types cannot be answered.
        </EmptyState>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Relationship</th>
                <th>Direction</th>
                <th>From → to</th>
                <th>Attributes</th>
                <th>Instances</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map(({ edge, direction }) => (
                <tr key={`${direction}-${edge.edge_type_id}`}>
                  <td className="mono">
                    <strong>{edge.key}</strong>
                  </td>
                  <td>
                    <span className="badge badge-neutral">
                      {direction === 'out' ? 'outgoing' : 'incoming'}
                    </span>
                  </td>
                  <td>
                    {edge.from_node_type} → {edge.to_node_type}
                  </td>
                  <td>

                  </td>
                  <td className="mono small">
                    {edge.attributes.length
                      ? edge.attributes.map((a) => `${a.key}: ${a.data_type}`).join(', ')
                      : '—'}
                  </td>
                  <td>{(edge.instance_count ?? 0).toLocaleString()}</td>
                  <td>
                    <div className="row-tight">
                      <button className="btn btn-ghost btn-sm" onClick={() => setEditing(edge)}>
                        Edit
                      </button>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={async () => {
                          if (
                            !window.confirm(
                              `Delete '${edge.key}'? Existing links of this type stay in the data but stop being part of the model.`,
                            )
                          )
                            return
                          try {
                            await remove.mutateAsync(edge.edge_type_id)
                            toast.success(`Deleted ${edge.key}.`)
                          } catch (error) {
                            toast.error(error)
                          }
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <EdgeTypeDialog
          graphId={graphId}
          nodeTypes={nodeTypes}
          defaultFrom={detail.node_type.key}
          existing={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  )
}

export function EdgeTypeDialog({
  graphId,
  nodeTypes,
  defaultFrom,
  existing,
  onClose,
}: {
  graphId: string
  nodeTypes: NodeTypeDef[]
  defaultFrom?: string
  existing?: EdgeTypeDef
  onClose: () => void
}) {
  const { create, update } = useEdgeTypeMutations(graphId)
  const toast = useToast()

  const [key, setKey] = useState(existing?.key ?? '')
  const [from, setFrom] = useState(existing?.from_node_type ?? defaultFrom ?? nodeTypes[0]?.key ?? '')
  const [to, setTo] = useState(existing?.to_node_type ?? nodeTypes[0]?.key ?? '')
  const [description, setDescription] = useState(existing?.description ?? '')
  const [attributes, setAttributes] = useState<{ key: string; data_type: DataType }[]>(
    existing?.attributes.map((a) => ({ key: a.key, data_type: a.data_type })) ?? [],
  )

  const busy = create.isPending || update.isPending

  async function save() {
    const body = {
      key: key.trim(),
      from_node_type: from,
      to_node_type: to,
      description: description.trim() || null,
      attributes: attributes.filter((a) => a.key.trim()),
    }
    try {
      if (existing) await update.mutateAsync({ id: existing.edge_type_id, body })
      else await create.mutateAsync(body)
      toast.success(`Saved ${body.key}.`)
      onClose()
    } catch (error) {
      toast.error(error)
    }
  }

  return (
    <Modal
      title={existing ? `Edit ${existing.key}` : 'New relationship'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={save}
            disabled={busy || !key.trim() || !from || !to}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div className="stack">
        <Field label="Name" required help="Stored as SCREAMING_SNAKE — 'has contact' becomes HAS_CONTACT.">
          <input
            className="input"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="HAS_CONTACT"
          />
        </Field>

        <div className="field-pair">
          <Field label="From" required>
            <select className="select" value={from} onChange={(e) => setFrom(e.target.value)}>
              {nodeTypes.map((n) => (
                <option key={n.node_type_id} value={n.key}>
                  {n.key}
                </option>
              ))}
            </select>
          </Field>
          <Field label="To" required>
            <select className="select" value={to} onChange={(e) => setTo(e.target.value)}>
              {nodeTypes.map((n) => (
                <option key={n.node_type_id} value={n.key}>
                  {n.key}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {/* Every relationship is many-to-many. Records are matched and merged
            after the fact, so "one" is not something the graph could enforce
            without silently dropping an edge somebody asserted — and a control
            with one valid answer is a question nobody should be asked. */}
        <p className="small faint" style={{ margin: 0 }}>
          {describeCardinality(from, to)}
        </p>

        <Field
          label="Attributes"
          help="Facts about the link itself rather than either end — a role, a date, a sentiment."
        >
          <div className="stack" style={{ gap: 6 }}>
            {attributes.map((attribute, index) => (
              <div className="array-row" key={index}>
                <input
                  className="input"
                  value={attribute.key}
                  placeholder="role"
                  onChange={(e) =>
                    setAttributes((list) =>
                      list.map((a, i) => (i === index ? { ...a, key: e.target.value } : a)),
                    )
                  }
                />
                <select
                  className="select"
                  style={{ maxWidth: 140 }}
                  value={attribute.data_type}
                  onChange={(e) =>
                    setAttributes((list) =>
                      list.map((a, i) =>
                        i === index ? { ...a, data_type: e.target.value as DataType } : a,
                      ),
                    )
                  }
                >
                  {DATA_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => setAttributes((list) => list.filter((_, i) => i !== index))}
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              className="btn btn-sm"
              onClick={() => setAttributes((list) => [...list, { key: '', data_type: 'string' }])}
            >
              Add attribute
            </button>
          </div>
        </Field>

        <Field label="What does it mean?">
          <input
            className="input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="The people we deal with at this account."
          />
        </Field>
      </div>
    </Modal>
  )
}

/** The one sentence the removed control used to be for. */
function describeCardinality(from: string, to: string): string {
  const left = from || 'record'
  const right = to || 'record'
  return `Many-to-many: many ${left} records can link to many ${right} records, and the other way round.`
}
